import React, { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Loader2, ShoppingCart } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ShopifyLab() {
  const [shopDomain, setShopDomain] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [limit, setLimit] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const fetchOrders = async () => {
    try {
      if (!shopDomain || !accessToken) {
        toast.error('Enter shop domain and access token');
        return;
      }
      setIsLoading(true);
      setRows([]);
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch('https://api.glowytics.com/api/shopify/labs/orders', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId || ''
        },
        body: JSON.stringify({
          shopDomain,
          accessToken,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          limit: Number(limit) || 50
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to fetch orders');
      setRows(Array.isArray(data.rows) ? data.rows : []);
      toast.success(`Fetched ${Array.isArray(data.rows) ? data.rows.length : 0} line items`);
    } catch (e) {
      console.error('Shopify fetch error', e);
      toast.error(e.message || 'Shopify fetch failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center gap-2">
        <ShoppingCart className="w-5 h-5 text-emerald-600" />
        <h1 className="text-lg font-semibold text-gray-800">Shopify Lab — Orders test</h1>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Shop domain</label>
            <input
              type="text"
              placeholder="yourstore or yourstore.myshopify.com"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={shopDomain}
              onChange={(e) => setShopDomain(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Admin Access Token</label>
            <input
              type="password"
              placeholder="shpat_xxx"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Limit</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              min={1}
              max={250}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Start Date (optional)</label>
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">End Date (optional)</label>
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-3">
          <Button
            className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl px-4"
            onClick={fetchOrders}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Fetching…
              </span>
            ) : (
              'Fetch Orders'
            )}
          </Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-left px-3 py-2">Customer ID</th>
                <th className="text-left px-3 py-2">Channel</th>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">Brand</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-right px-3 py-2">Price</th>
                <th className="text-right px-3 py-2">Final Price</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className="px-3 py-4 text-center text-gray-500" colSpan={10}>No data</td>
                </tr>
              ) : rows.map((r, idx) => (
                <tr key={`${r.order_id}-${r.line_item_id}-${idx}`} className="border-t">
                  <td className="px-3 py-2">{r.order_number ? `#${r.order_number}` : r.order_id}</td>
                  <td className="px-3 py-2">{r.order_date ? new Date(r.order_date).toLocaleString() : '-'}</td>
                  <td className="px-3 py-2">{r.customer_id || '-'}</td>
                  <td className="px-3 py-2">{r.sales_channel_name || '-'}</td>
                  <td className="px-3 py-2">{r.sku || '-'}</td>
                  <td className="px-3 py-2">{r.product_name || '-'}</td>
                  <td className="px-3 py-2">{r.brand_name || '-'}</td>
                  <td className="px-3 py-2 text-right">{Number(r.quantity || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{Number(r.price || 0).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{Number(r.final_price || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


